// WHY: per-item photos are uploaded via a presigned PUT and the storage
// scope's 10 MB cap rejects raw camera output. A small canvas-based
// resize keeps the largest edge ≤ 1920 px and re-encodes JPEG at
// quality 0.85 — no production dependency, no detail loss visible at
// the drawer's max preview size. Failures fall through as a typed
// rejection so the caller surfaces a localized inline error rather than
// silently uploading a 30 MB original.

const MAX_EDGE = 1920;
const JPEG_QUALITY = 0.85;

export type PhotoCompressError =
  | { code: 'unsupported-source' }
  | { code: 'decode-failed' }
  | { code: 'encode-failed' };

// DANGER (#1196/#1244): untrusted SVG stays outside the browser-native raster
// decoder path until the product has an explicit sanitization contract.
export function isUnsupportedSvgImage(source: { type: string; name?: string }): boolean {
  const mime = source.type.split(';', 1)[0]?.trim().toLowerCase() ?? '';
  return mime === 'image/svg+xml' || (mime === '' && /\.svg$/i.test(source.name ?? ''));
}

export function imageSourceFormat(file: File): string {
  const mime = file.type.trim();
  if (mime !== '') return mime;
  const extension = /\.([^.]+)$/.exec(file.name)?.[1];
  return extension ? `.${extension.toLowerCase()}` : file.name;
}

export function isPhotoCompressError(error: unknown): error is PhotoCompressError {
  if (typeof error !== 'object' || error === null || !('code' in error)) return false;
  return (
    error.code === 'unsupported-source' ||
    error.code === 'decode-failed' ||
    error.code === 'encode-failed'
  );
}

export async function compressImage(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const { width, height } = fit(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw { code: 'unsupported-source' } satisfies PhotoCompressError;
    // WHY: JPEG has no alpha channel. Without an explicit background
    // fill, transparent regions of clipboard PNG screenshots flatten
    // unpredictably (often black in Chromium). Fill white before draw
    // so the encoded JPEG matches what the user copied.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((res) =>
      canvas.toBlob(res, 'image/jpeg', JPEG_QUALITY),
    );
    if (!blob) throw { code: 'encode-failed' } satisfies PhotoCompressError;
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function fit(w: number, h: number): { width: number; height: number } {
  const longest = Math.max(w, h);
  if (longest <= MAX_EDGE) return { width: w, height: h };
  const scale = MAX_EDGE / longest;
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject({ code: 'decode-failed' } satisfies PhotoCompressError);
    img.src = url;
  });
}
